import type { HTMLAttributes, ReactNode } from 'react';
import { cn } from './utils';

export type ChipProps = HTMLAttributes<HTMLSpanElement> & {
  /** Pre-styled label tone. */
  tone?: 'neutral' | 'gold' | 'emerald' | 'rose' | 'ink';
  /** When true, content is rendered uppercase + tracked, like a section caption. */
  uppercase?: boolean;
  leadingIcon?: ReactNode;
  children: ReactNode;
};

const TONE_CLS = {
  neutral: 'bg-white/36 border-white/60 text-ink-700 backdrop-blur',
  gold: 'bg-gold-50 border-gold-200 text-gold-600',
  emerald: 'border text-[color:var(--color-emerald)]',
  rose: 'border text-[color:var(--color-rose)]',
  ink: 'bg-ink-900 border-ink-900 text-white',
} as const;

/** Tiny inline label / status pill. */
export function Chip({
  tone = 'neutral',
  uppercase,
  leadingIcon,
  children,
  className,
  style,
  ...rest
}: ChipProps) {
  return (
    <span
      {...rest}
      className={cn(
        'inline-flex items-center gap-1.5 px-1.5 py-0.5 rounded-full text-[10.5px] font-semibold border leading-none',
        TONE_CLS[tone],
        uppercase && 'uppercase tracking-[0.16em] text-[9.5px]',
        className,
      )}
      style={
        tone === 'emerald'
          ? { background: 'rgba(47,125,91,0.08)', borderColor: 'rgba(47,125,91,0.25)', ...style }
          : tone === 'rose'
            ? { background: 'rgba(184,91,79,0.08)', borderColor: 'rgba(184,91,79,0.25)', ...style }
            : style
      }
    >
      {leadingIcon}
      {children}
    </span>
  );
}
